import {
  addHovAmplifierGaugeVisualCharges,
  applyHovAmplifierCumulativeBoostGain,
  chargeHovAmplifierLowerGaugeFromDrain,
  drainHovAmplifierGauge,
  drainHovAmplifierGaugeVisualSegments,
  HOV_AMPLIFIER_ENTRY_DEFINITIONS,
  increaseHovAmplifierGauge,
  isHovAmplifierEntryUnlocked,
  resolveHovAmplifierDrainTarget,
  resolveHovAmplifierDrainTargetGainFromGauge,
  resolveHovAmplifierDrainTargetGainPerGauge,
  resolveHovAmplifierEffectiveGainPerSecond,
  resolveHovAmplifierEntryGainMultiplier,
  resolveHovAmplifierChallengeState,
  resolveHovAmplifierGainMultiplier,
  resolveHovAmplifierGainPerSecond,
  resolveHovAmplifierVisualChargeColor,
  syncHovAmplifierGaugeVisualSegmentsToGaugeValues,
  type HovAmplifierColorKey,
  type HovAmplifierEntryId,
  type HovAmplifierEntryAvailabilityContext,
  type HovAmplifierChallengeState,
  type HovAmplifierGaugeVisualChargeBatch,
  type HovAmplifierGaugeVisualSegments,
  type HovAmplifierRecentChargeSources,
  type HovAmplifierRecentDirectChargeAtMs,
  type HovAmplifierState,
} from "@/features/hoverIdle/hov/hovAmplifierModel";
import type { WallAmplifierChallenge } from "@/features/hoverIdle/wall/wallChallengePolicy";
import { compareHuge, type HugeNumber } from "@/shared/numbers";

// Pure tick resolver for the Hov Amplifier subsystem.
//
// `HoverIdleModel` owns authoritative Amplifier state and drives orchestration;
// this resolver owns the rules for how a single tick advances that state:
// direct-charge fills, drain into AP / cumulative boosts, lower-gauge
// support charge, and the visual segment / recent-source bookkeeping that
// downstream snapshots read. The resolver does not touch model fields, the
// DOM, or any other subsystem; the caller adopts `result.gameplay` and
// `result.display` as the next authoritative state.

export interface HovAmplifierTickInput {
  readonly state: HovAmplifierState;
  readonly visualSegments: HovAmplifierGaugeVisualSegments;
  readonly recentChargeSources: HovAmplifierRecentChargeSources;
  readonly recentDirectChargeAtMs: HovAmplifierRecentDirectChargeAtMs;
  readonly dtSeconds: number;
  readonly nowMs: number;
  // The entry the player is currently hovering, if any. Manual hover fill is
  // skipped when the same entry is also driven by a direct MP assignment to
  // avoid double-charging within a single tick.
  readonly hoveringEntryId: HovAmplifierEntryId | null;
  readonly mpAssignedEntryIds: readonly HovAmplifierEntryId[];
  // Long catchup steps may delay drain until a directly charged gauge first
  // reaches full; normal online ticks keep their existing immediate mode.
  readonly directChargeDrainMode?: "immediate" | "after-full";
  // Composed source-Gain multipliers per entry (OHov × skill etc.). Missing
  // entries are treated as x1. The same map shape is consumed by the
  // snapshot builder so callers can reuse one precomputed value per tick.
  readonly amplifierGainMultipliersByEntry: Partial<
    Record<HovAmplifierEntryId, HugeNumber>
  >;
  readonly availability?: HovAmplifierEntryAvailabilityContext;
  readonly amplifierChallenge?: WallAmplifierChallenge | null;
}

export interface HovAmplifierTickGameplayResult {
  readonly state: HovAmplifierState;
  readonly fullChargeReachedEntryIds: readonly HovAmplifierEntryId[];
}

export interface HovAmplifierTickDisplayResult {
  readonly visualSegments: HovAmplifierGaugeVisualSegments;
  readonly recentChargeSources: HovAmplifierRecentChargeSources;
  readonly recentDirectChargeAtMs: HovAmplifierRecentDirectChargeAtMs;
}

export interface HovAmplifierTickResult {
  readonly gameplay: HovAmplifierTickGameplayResult;
  readonly display: HovAmplifierTickDisplayResult;
}

export function resolveHovAmplifierTick(
  input: HovAmplifierTickInput,
): HovAmplifierTickResult {
  const working = createInitialWorkingState(input);

  if (Number.isFinite(input.dtSeconds) && input.dtSeconds > 0) {
    applyDirectChargeFills(working, input);
    applyDrainTick(working, input);
  }

  flushVisualChargeBatch(working);

  return {
    gameplay: {
      state: working.state,
      fullChargeReachedEntryIds: [...working.fullChargeReachedEntryIds],
    },
    display: {
      visualSegments: working.visualSegments,
      recentChargeSources: working.recentChargeSources,
      recentDirectChargeAtMs: working.recentDirectChargeAtMs,
    },
  };
}

interface WorkingState {
  state: HovAmplifierState;
  fullChargeReachedEntryIds: Set<HovAmplifierEntryId>;
  visualSegments: HovAmplifierGaugeVisualSegments;
  recentChargeSources: HovAmplifierRecentChargeSources;
  recentDirectChargeAtMs: HovAmplifierRecentDirectChargeAtMs;
  visualChargeBatch: HovAmplifierGaugeVisualChargeBatch;
  challengeState: HovAmplifierChallengeState;
}

function createInitialWorkingState(input: HovAmplifierTickInput): WorkingState {
  return {
    state: input.state,
    fullChargeReachedEntryIds: new Set(),
    visualSegments: input.visualSegments,
    recentChargeSources: input.recentChargeSources,
    recentDirectChargeAtMs: input.recentDirectChargeAtMs,
    visualChargeBatch: {},
    challengeState: resolveHovAmplifierChallengeState(input.state, {
      challenge: input.amplifierChallenge ?? null,
      availability: input.availability,
    }),
  };
}

function applyDirectChargeFills(
  working: WorkingState,
  input: HovAmplifierTickInput,
): void {
  applyHoverFill(working, input);
  applyMpAssignmentFills(working, input);
}

function applyHoverFill(
  working: WorkingState,
  input: HovAmplifierTickInput,
): void {
  const entryId = input.hoveringEntryId;
  if (entryId === null) return;
  if (input.mpAssignedEntryIds.includes(entryId)) return;
  applyEntryDirectCharge(working, entryId, input.dtSeconds, input.nowMs);
}

function applyMpAssignmentFills(
  working: WorkingState,
  input: HovAmplifierTickInput,
): void {
  for (const entryId of input.mpAssignedEntryIds) {
    applyEntryDirectCharge(working, entryId, input.dtSeconds, input.nowMs);
  }
}

function applyEntryDirectCharge(
  working: WorkingState,
  entryId: HovAmplifierEntryId,
  dtSeconds: number,
  nowMs: number,
): void {
  if (working.challengeState.corruptedEntryIds.has(entryId)) return;
  const beforeGauge = resolveGaugeValueFromState(working.state, entryId);
  const nextState = increaseHovAmplifierGauge(
    working.state,
    entryId,
    dtSeconds,
  );
  if (nextState === working.state) return;
  working.state = nextState;
  recordFullChargeReached(working, entryId, beforeGauge);
  recordRecentDirectCharge(working, entryId, nowMs);
  const visualAmount =
    resolveGaugeValueFromState(working.state, entryId) - beforeGauge;
  queueVisualCharge(working, entryId, entryId, visualAmount);
}

function applyDrainTick(
  working: WorkingState,
  input: HovAmplifierTickInput,
): void {
  let nextAp = working.state.ap;
  let mutated = false;

  for (const definition of HOV_AMPLIFIER_ENTRY_DEFINITIONS) {
    if (working.challengeState.corruptedEntryIds.has(definition.id)) continue;
    if (!isHovAmplifierEntryUnlocked(working.state, definition.id)) continue;
    const entry = working.state.entries.find(
      (candidate) => candidate.id === definition.id,
    );
    if (!entry) continue;

    const drainOutcome = drainGaugeAndConsumeVisualSegments(
      working,
      definition.id,
      resolveDrainSecondsForEntry(input, definition.id),
    );
    if (drainOutcome.gameplayMutated) {
      mutated = true;
    }
    if (drainOutcome.actualDrain <= 0) continue;

    const targetGain = resolveTargetGainForDrain({
      state: working.state,
      definition,
      enhancementLevel: entry.enhancementLevel,
      actualDrain: drainOutcome.actualDrain,
      amplifierGainMultipliersByEntry: input.amplifierGainMultipliersByEntry,
      challengeSourceGainMultiplier:
        working.challengeState.sourceGainMultipliersByEntry[definition.id],
    });
    if (targetGain === null) continue;

    const target = resolveHovAmplifierDrainTarget(definition.id);
    if (target.kind === "ap") {
      nextAp = nextAp.add(targetGain);
    } else {
      working.state = applyHovAmplifierCumulativeBoostGain(
        working.state,
        target.entryId,
        targetGain,
      );
    }

    applyLowerGaugeSupportCharge(working, {
      sourceEntryId: definition.id,
      actualDrain: drainOutcome.actualDrain,
      consumedVisualSource: drainOutcome.consumedVisualSource,
    });

    mutated = true;
  }

  if (mutated) {
    working.state = { ...working.state, ap: nextAp };
    clearDisplayStateForEmptyEntries(working);
  }
}

function resolveDrainSecondsForEntry(
  input: HovAmplifierTickInput,
  entryId: HovAmplifierEntryId,
): number {
  if (input.directChargeDrainMode !== "after-full") {
    return input.dtSeconds;
  }
  const isDirectlyCharged =
    input.mpAssignedEntryIds.includes(entryId) ||
    input.hoveringEntryId === entryId;
  if (!isDirectlyCharged) {
    return input.dtSeconds;
  }
  const definition = HOV_AMPLIFIER_ENTRY_DEFINITIONS.find(
    (candidate) => candidate.id === entryId,
  );
  if (definition === undefined || definition.baseFillRatePerSecond <= 0) {
    return input.dtSeconds;
  }
  const initialGauge = resolveGaugeValueFromState(input.state, entryId);
  const secondsUntilFull =
    (1 - initialGauge) / definition.baseFillRatePerSecond;
  return Math.max(0, input.dtSeconds - secondsUntilFull);
}

interface DrainOutcome {
  readonly actualDrain: number;
  readonly consumedVisualSource: HovAmplifierColorKey | null;
  readonly gameplayMutated: boolean;
}

function drainGaugeAndConsumeVisualSegments(
  working: WorkingState,
  entryId: HovAmplifierEntryId,
  dtSeconds: number,
): DrainOutcome {
  const result = drainHovAmplifierGauge(working.state, entryId, dtSeconds);
  if (result.state === working.state) {
    return {
      actualDrain: result.actualDrain,
      consumedVisualSource: null,
      gameplayMutated: false,
    };
  }
  working.state = result.state;
  const visualDrain = drainHovAmplifierGaugeVisualSegments(
    working.visualSegments,
    entryId,
    result.actualDrain,
  );
  working.visualSegments = visualDrain.segmentsByEntry;
  return {
    actualDrain: result.actualDrain,
    consumedVisualSource: visualDrain.consumedVisualSource,
    gameplayMutated: true,
  };
}

interface ResolveTargetGainInput {
  readonly state: HovAmplifierState;
  readonly definition: (typeof HOV_AMPLIFIER_ENTRY_DEFINITIONS)[number];
  readonly enhancementLevel: number;
  readonly actualDrain: number;
  readonly amplifierGainMultipliersByEntry: Partial<
    Record<HovAmplifierEntryId, HugeNumber>
  >;
  readonly challengeSourceGainMultiplier: number;
}

function resolveTargetGainForDrain(
  input: ResolveTargetGainInput,
): HugeNumber | null {
  const baseGainPerSecond = resolveHovAmplifierGainPerSecond(
    input.enhancementLevel,
  );
  const cumulativeMultiplier = resolveHovAmplifierGainMultiplier(
    input.state,
    input.definition.id,
  );
  const entryGainMultiplier = resolveHovAmplifierEntryGainMultiplier(
    input.amplifierGainMultipliersByEntry,
    input.definition.id,
  );
  const sourceGainPerSecond = resolveHovAmplifierEffectiveGainPerSecond(
    baseGainPerSecond,
    cumulativeMultiplier
      .mul(entryGainMultiplier)
      .mul(input.challengeSourceGainMultiplier),
  );
  const targetGainPerGauge = resolveHovAmplifierDrainTargetGainPerGauge(
    sourceGainPerSecond,
    input.definition.baseDrainRatePerSecond,
  );
  const targetGain = resolveHovAmplifierDrainTargetGainFromGauge(
    input.actualDrain,
    targetGainPerGauge,
  );
  if (compareHuge(targetGain, 0) <= 0) return null;
  return targetGain;
}

interface ApplyLowerGaugeInput {
  readonly sourceEntryId: HovAmplifierEntryId;
  readonly actualDrain: number;
  readonly consumedVisualSource: HovAmplifierColorKey | null;
}

function applyLowerGaugeSupportCharge(
  working: WorkingState,
  input: ApplyLowerGaugeInput,
): void {
  const chargedState = chargeHovAmplifierLowerGaugeFromDrain(
    working.state,
    input.sourceEntryId,
    input.actualDrain,
  );
  if (chargedState === working.state) return;

  const definition = HOV_AMPLIFIER_ENTRY_DEFINITIONS.find(
    (candidate) => candidate.id === input.sourceEntryId,
  );
  const chargedEntryId = definition?.feedsGaugeForEntryId ?? null;
  const beforeChargedGauge =
    chargedEntryId === null
      ? 0
      : resolveGaugeValueFromState(working.state, chargedEntryId);
  const visualAmount =
    chargedEntryId === null
      ? 0
      : resolveGaugeValueFromState(chargedState, chargedEntryId) -
        beforeChargedGauge;

  working.state = chargedState;
  if (chargedEntryId === null) return;
  recordFullChargeReached(working, chargedEntryId, beforeChargedGauge);

  recordRecentChargeSource(working, chargedEntryId, input.sourceEntryId);
  const propagatedVisualSource =
    input.consumedVisualSource ??
    resolveHovAmplifierVisualChargeColor(
      input.sourceEntryId,
      working.recentChargeSources,
    );
  queueVisualCharge(
    working,
    chargedEntryId,
    propagatedVisualSource,
    visualAmount,
  );
}

function recordFullChargeReached(
  working: WorkingState,
  entryId: HovAmplifierEntryId,
  beforeGauge: number,
): void {
  const afterGauge = resolveGaugeValueFromState(working.state, entryId);
  if (beforeGauge < 1 && afterGauge >= 1) {
    working.fullChargeReachedEntryIds.add(entryId);
  }
}

function clearDisplayStateForEmptyEntries(working: WorkingState): void {
  for (const entry of working.state.entries) {
    if (entry.gaugeValue > 0) continue;
    clearRecentChargeSourceForEntry(working, entry.id);
    clearRecentDirectChargeForEntry(working, entry.id);
    clearVisualSegmentsForEntry(working, entry.id);
  }
}

function flushVisualChargeBatch(working: WorkingState): void {
  working.visualSegments = addHovAmplifierGaugeVisualCharges(
    working.visualSegments,
    working.visualChargeBatch,
  );
  working.visualSegments = syncHovAmplifierGaugeVisualSegmentsToGaugeValues(
    working.visualSegments,
    working.state,
  );
  for (const entryId of working.challengeState.corruptedEntryIds) {
    clearRecentChargeSourceForEntry(working, entryId);
    clearRecentDirectChargeForEntry(working, entryId);
    clearVisualSegmentsForEntry(working, entryId);
  }
  working.visualChargeBatch = {};
}

function queueVisualCharge(
  working: WorkingState,
  entryId: HovAmplifierEntryId,
  sourceColorKey: HovAmplifierColorKey,
  amount: number,
): void {
  if (!Number.isFinite(amount) || amount <= 0) return;
  working.visualChargeBatch = {
    ...working.visualChargeBatch,
    [entryId]: [
      ...(working.visualChargeBatch[entryId] ?? []),
      { amount, sourceColorKey },
    ],
  };
}

function recordRecentDirectCharge(
  working: WorkingState,
  entryId: HovAmplifierEntryId,
  nowMs: number,
): void {
  if (!Number.isFinite(nowMs)) return;
  working.recentDirectChargeAtMs = {
    ...working.recentDirectChargeAtMs,
    [entryId]: nowMs,
  };
}

function recordRecentChargeSource(
  working: WorkingState,
  chargedEntryId: HovAmplifierEntryId,
  sourceEntryId: HovAmplifierEntryId,
): void {
  if (working.recentChargeSources[chargedEntryId] === sourceEntryId) return;
  working.recentChargeSources = {
    ...working.recentChargeSources,
    [chargedEntryId]: sourceEntryId,
  };
}

function clearRecentChargeSourceForEntry(
  working: WorkingState,
  entryId: HovAmplifierEntryId,
): void {
  if ((working.recentChargeSources[entryId] ?? null) === null) return;
  working.recentChargeSources = {
    ...working.recentChargeSources,
    [entryId]: null,
  };
}

function clearRecentDirectChargeForEntry(
  working: WorkingState,
  entryId: HovAmplifierEntryId,
): void {
  if (working.recentDirectChargeAtMs[entryId] === undefined) return;
  const next = { ...working.recentDirectChargeAtMs };
  delete next[entryId];
  working.recentDirectChargeAtMs = next;
}

function clearVisualSegmentsForEntry(
  working: WorkingState,
  entryId: HovAmplifierEntryId,
): void {
  if ((working.visualSegments[entryId] ?? []).length === 0) return;
  working.visualSegments = {
    ...working.visualSegments,
    [entryId]: [],
  };
}

function resolveGaugeValueFromState(
  state: HovAmplifierState,
  entryId: HovAmplifierEntryId,
): number {
  const value =
    state.entries.find((entry) => entry.id === entryId)?.gaugeValue ?? 0;
  if (!Number.isFinite(value)) return 0;
  if (value <= 0) return 0;
  if (value >= 1) return 1;
  return value;
}
